import { SECTOR_LABELS, SECTOR_SLUGS } from '#/lib/category-navigation';
import type { IndustrySlug } from '#/types';

export type FacetOption = {
  value: string;
  label: string;
};

export type SearchMarketCapCategory =
  | 'mega-cap'
  | 'large-cap'
  | 'mid-cap'
  | 'small-cap'
  | 'micro-cap';

export type SearchExchangeCode =
  | 'NYSE'
  | 'NAS'
  | 'ARCA'
  | 'AMEX'
  | 'BATS'
  | 'IEXG'
  | 'OTCM'
  | 'OTCBB'
  | 'OTCPK'
  | 'GREY';

export type SearchExchangeGroup = {
  value: string;
  label: string;
  exchanges: Array<{
    value: SearchExchangeCode;
    label: string;
  }>;
};

export type SearchEquitySectorGroup = {
  value: string;
  label: string;
  industries: Array<{
    value: IndustrySlug;
    label: string;
  }>;
};

export const SEARCH_MARKET_CAP_CATEGORIES: SearchMarketCapCategory[] = [
  'mega-cap',
  'large-cap',
  'mid-cap',
  'small-cap',
  'micro-cap',
];

export const EXCHANGE_GROUPS: SearchExchangeGroup[] = [
  {
    value: 'listed',
    label: 'Listed',
    exchanges: [
      { value: 'NYSE', label: 'New York Stock Exchange' },
      { value: 'NAS', label: 'Nasdaq' },
      { value: 'ARCA', label: 'NYSE Arca' },
      { value: 'AMEX', label: 'NYSE American (AMEX)' },
      { value: 'BATS', label: 'Cboe BZX Exchange (BATS)' },
      { value: 'IEXG', label: 'IEX (Investors Exchange)' },
    ],
  },
  {
    value: 'otc',
    label: 'OTC',
    exchanges: [
      { value: 'OTCM', label: 'OTC Markets' },
      { value: 'OTCBB', label: 'OTC Bulletin Board' },
      { value: 'OTCPK', label: 'OTC Pink (Pink Sheets)' },
      { value: 'GREY', label: 'Grey Market' },
    ],
  },
];

export const SEARCH_EXCHANGE_CODES = EXCHANGE_GROUPS.flatMap((group) =>
  group.exchanges.map((exchange) => exchange.value),
);

export const LISTING_TYPE_OPTIONS: FacetOption[] = [
  { value: 'all', label: 'All Exchanges' },
  { value: 'listed', label: 'Listed Exchanges' },
  { value: 'otc', label: 'OTC Exchanges' },
];

export const SEARCH_MARKET_CAP_CATEGORY_LABELS: Record<
  SearchMarketCapCategory,
  string
> = {
  'mega-cap': 'Mega Cap',
  'large-cap': 'Large Cap',
  'mid-cap': 'Mid Cap',
  'small-cap': 'Small Cap',
  'micro-cap': 'Micro Cap',
};

export const FUND_CATEGORY_LABELS: Record<string, string> = {
  'basic-materials': 'Basic Materials',
  'communication-services': 'Communication Services',
  'consumer-cyclical': 'Consumer Cyclical',
  'consumer-defensive': 'Consumer Defensive',
  energy: 'Energy',
  'financial-services': 'Financial Services',
  healthcare: 'Healthcare',
  industrials: 'Industrials',
  'real-estate': 'Real Estate',
  technology: 'Technology',
  utilities: 'Utilities',
  'large-cap-value': 'Large Cap Value',
  'large-cap-blend': 'Large Cap Blend',
  'large-cap-growth': 'Large Cap Growth',
  'mid-cap-value': 'Mid Cap Value',
  'mid-cap-blend': 'Mid Cap Blend',
  'mid-cap-growth': 'Mid Cap Growth',
  'small-cap-value': 'Small Cap Value',
  'small-cap-blend': 'Small Cap Blend',
  'small-cap-growth': 'Small Cap Growth',
};

export const EQUITY_SECTOR_OPTIONS: FacetOption[] = [
  { value: '', label: 'Any Sector' },
  ...SECTOR_SLUGS.map((slug) => ({
    value: slug,
    label: SECTOR_LABELS[slug],
  })),
];

export const EQUITY_INDUSTRY_GROUPS: SearchEquitySectorGroup[] = [
  {
    value: 'basic-materials',
    label: 'Basic Materials',
    industries: [
      { value: 'agricultural-inputs', label: 'Agricultural Inputs' },
      { value: 'building-materials', label: 'Building Materials' },
      { value: 'chemicals', label: 'Chemicals' },
      { value: 'specialty-chemicals', label: 'Specialty Chemicals' },
      {
        value: 'lumber-wood-production',
        label: 'Lumber & Wood Production',
      },
      {
        value: 'paper-paper-products',
        label: 'Paper & Paper Products',
      },
      { value: 'aluminum', label: 'Aluminum' },
      { value: 'copper', label: 'Copper' },
      {
        value: 'other-industrial-metals-mining',
        label: 'Other Industrial Metals & Mining',
      },
      { value: 'gold', label: 'Gold' },
      { value: 'silver', label: 'Silver' },
      {
        value: 'other-precious-metals-mining',
        label: 'Other Precious Metals & Mining',
      },
      { value: 'coking-coal', label: 'Coking Coal' },
      { value: 'steel', label: 'Steel' },
    ],
  },
  {
    value: 'communication-services',
    label: 'Communication Services',
    industries: [
      { value: 'telecom-services', label: 'Telecom Services' },
      { value: 'advertising-agencies', label: 'Advertising Agencies' },
      { value: 'publishing', label: 'Publishing' },
      { value: 'broadcasting', label: 'Broadcasting' },
      { value: 'entertainment', label: 'Entertainment' },
      {
        value: 'internet-content-information',
        label: 'Internet Content & Information',
      },
      {
        value: 'electronic-gaming-multimedia',
        label: 'Electronic Gaming & Multimedia',
      },
    ],
  },
  {
    value: 'consumer-cyclical',
    label: 'Consumer Cyclical',
    industries: [
      { value: 'auto-truck-dealerships', label: 'Auto & Truck Dealerships' },
      { value: 'auto-manufacturers', label: 'Auto Manufacturers' },
      { value: 'auto-parts', label: 'Auto Parts' },
      { value: 'recreational-vehicles', label: 'Recreational Vehicles' },
      {
        value: 'furnishings-fixtures-appliances',
        label: 'Furnishings, Fixtures & Appliances',
      },
      {
        value: 'residential-construction',
        label: 'Residential Construction',
      },
      { value: 'textile-manufacturing', label: 'Textile Manufacturing' },
      { value: 'apparel-manufacturing', label: 'Apparel Manufacturing' },
      { value: 'footwear-accessories', label: 'Footwear & Accessories' },
      { value: 'packaging-containers', label: 'Packaging & Containers' },
      { value: 'personal-services', label: 'Personal Services' },
      { value: 'restaurants', label: 'Restaurants' },
      { value: 'apparel-retail', label: 'Apparel Retail' },
      { value: 'department-stores', label: 'Department Stores' },
      {
        value: 'home-improvement-retail',
        label: 'Home Improvement Retail',
      },
      { value: 'luxury-goods', label: 'Luxury Goods' },
      { value: 'internet-retail', label: 'Internet Retail' },
      { value: 'specialty-retail', label: 'Specialty Retail' },
      { value: 'gambling', label: 'Gambling' },
      { value: 'leisure', label: 'Leisure' },
      { value: 'lodging', label: 'Lodging' },
      { value: 'resorts-casinos', label: 'Resorts & Casinos' },
      { value: 'travel-services', label: 'Travel Services' },
    ],
  },
  {
    value: 'consumer-defensive',
    label: 'Consumer Defensive',
    industries: [
      { value: 'beverages-brewers', label: 'Beverages - Brewers' },
      {
        value: 'beverages-wineries-distilleries',
        label: 'Beverages - Wineries & Distilleries',
      },
      {
        value: 'beverages-non-alcoholic',
        label: 'Beverages - Non-Alcoholic',
      },
      { value: 'confectioners', label: 'Confectioners' },
      { value: 'farm-products', label: 'Farm Products' },
      {
        value: 'household-personal-products',
        label: 'Household & Personal Products',
      },
      { value: 'packaged-foods', label: 'Packaged Foods' },
      {
        value: 'education-training-services',
        label: 'Education & Training Services',
      },
      { value: 'discount-stores', label: 'Discount Stores' },
      { value: 'food-distribution', label: 'Food Distribution' },
      { value: 'grocery-stores', label: 'Grocery Stores' },
      { value: 'tobacco', label: 'Tobacco' },
    ],
  },
  {
    value: 'energy',
    label: 'Energy',
    industries: [
      { value: 'oil-gas-drilling', label: 'Oil & Gas Drilling' },
      { value: 'oil-gas-ep', label: 'Oil & Gas E&P' },
      { value: 'oil-gas-integrated', label: 'Oil & Gas Integrated' },
      { value: 'oil-gas-midstream', label: 'Oil & Gas Midstream' },
      {
        value: 'oil-gas-refining-marketing',
        label: 'Oil & Gas Refining & Marketing',
      },
      {
        value: 'oil-gas-equipment-services',
        label: 'Oil & Gas Equipment & Services',
      },
      { value: 'thermal-coal', label: 'Thermal Coal' },
      { value: 'uranium', label: 'Uranium' },
    ],
  },
  {
    value: 'financial-services',
    label: 'Financial Services',
    industries: [
      { value: 'asset-management', label: 'Asset Management' },
      { value: 'banks-diversified', label: 'Banks - Diversified' },
      { value: 'banks-regional', label: 'Banks - Regional' },
      { value: 'mortgage-finance', label: 'Mortgage Finance' },
      { value: 'capital-markets', label: 'Capital Markets' },
      {
        value: 'financial-data-stock-exchanges',
        label: 'Financial Data & Stock Exchanges',
      },
      { value: 'insurance-life', label: 'Insurance - Life' },
      {
        value: 'insurance-property-casualty',
        label: 'Insurance - Property & Casualty',
      },
      {
        value: 'insurance-reinsurance',
        label: 'Insurance - Reinsurance',
      },
      { value: 'insurance-specialty', label: 'Insurance - Specialty' },
      { value: 'insurance-brokers', label: 'Insurance Brokers' },
      { value: 'insurance-diversified', label: 'Insurance - Diversified' },
      { value: 'shell-companies', label: 'Shell Companies' },
      {
        value: 'financial-conglomerates',
        label: 'Financial Conglomerates',
      },
      { value: 'credit-services', label: 'Credit Services' },
    ],
  },
  {
    value: 'healthcare',
    label: 'Healthcare',
    industries: [
      { value: 'biotechnology', label: 'Biotechnology' },
      {
        value: 'drug-manufacturers-general',
        label: 'Drug Manufacturers - General',
      },
      {
        value: 'drug-manufacturers-specialty-generic',
        label: 'Drug Manufacturers - Specialty & Generic',
      },
      { value: 'healthcare-plans', label: 'Healthcare Plans' },
      {
        value: 'medical-care-facilities',
        label: 'Medical Care Facilities',
      },
      {
        value: 'pharmaceutical-retailers',
        label: 'Pharmaceutical Retailers',
      },
      {
        value: 'health-information-services',
        label: 'Health Information Services',
      },
      { value: 'medical-devices', label: 'Medical Devices' },
      {
        value: 'medical-instruments-supplies',
        label: 'Medical Instruments & Supplies',
      },
      {
        value: 'diagnostics-research',
        label: 'Diagnostics & Research',
      },
      { value: 'medical-distribution', label: 'Medical Distribution' },
    ],
  },
  {
    value: 'industrials',
    label: 'Industrials',
    industries: [
      { value: 'aerospace-defense', label: 'Aerospace & Defense' },
      {
        value: 'specialty-business-services',
        label: 'Specialty Business Services',
      },
      { value: 'consulting-services', label: 'Consulting Services' },
      {
        value: 'rental-leasing-services',
        label: 'Rental & Leasing Services',
      },
      {
        value: 'security-protection-services',
        label: 'Security & Protection Services',
      },
      {
        value: 'staffing-employment-services',
        label: 'Staffing & Employment Services',
      },
      { value: 'conglomerates', label: 'Conglomerates' },
      {
        value: 'engineering-construction',
        label: 'Engineering & Construction',
      },
      {
        value: 'infrastructure-operations',
        label: 'Infrastructure Operations',
      },
      {
        value: 'building-products-equipment',
        label: 'Building Products & Equipment',
      },
      {
        value: 'farm-heavy-construction-machinery',
        label: 'Farm & Heavy Construction Machinery',
      },
      {
        value: 'industrial-distribution',
        label: 'Industrial Distribution',
      },
      {
        value: 'business-equipment-supplies',
        label: 'Business Equipment & Supplies',
      },
      {
        value: 'specialty-industrial-machinery',
        label: 'Specialty Industrial Machinery',
      },
      { value: 'metal-fabrication', label: 'Metal Fabrication' },
      {
        value: 'pollution-treatment-controls',
        label: 'Pollution & Treatment Controls',
      },
      { value: 'tools-accessories', label: 'Tools & Accessories' },
      {
        value: 'electrical-equipment-parts',
        label: 'Electrical Equipment & Parts',
      },
      {
        value: 'airports-air-services',
        label: 'Airports & Air Services',
      },
      { value: 'airlines', label: 'Airlines' },
      { value: 'railroads', label: 'Railroads' },
      { value: 'marine-shipping', label: 'Marine Shipping' },
      { value: 'trucking', label: 'Trucking' },
      {
        value: 'integrated-freight-logistics',
        label: 'Integrated Freight & Logistics',
      },
      { value: 'waste-management', label: 'Waste Management' },
    ],
  },
  {
    value: 'real-estate',
    label: 'Real Estate',
    industries: [
      {
        value: 'real-estate-development',
        label: 'Real Estate Development',
      },
      { value: 'real-estate-services', label: 'Real Estate Services' },
      {
        value: 'real-estate-diversified',
        label: 'Real Estate Diversified',
      },
      {
        value: 'reit-healthcare-facilities',
        label: 'REIT - Healthcare Facilities',
      },
      { value: 'reit-hotel-motel', label: 'REIT - Hotel & Motel' },
      { value: 'reit-industrial', label: 'REIT - Industrial' },
      { value: 'reit-office', label: 'REIT - Office' },
      { value: 'reit-residential', label: 'REIT - Residential' },
      { value: 'reit-retail', label: 'REIT - Retail' },
      { value: 'reit-mortgage', label: 'REIT - Mortgage' },
      { value: 'reit-specialty', label: 'REIT - Specialty' },
      { value: 'reit-diversified', label: 'REIT - Diversified' },
    ],
  },
  {
    value: 'technology',
    label: 'Technology',
    industries: [
      {
        value: 'information-technology-services',
        label: 'Information Technology Services',
      },
      { value: 'software-application', label: 'Software - Application' },
      {
        value: 'software-infrastructure',
        label: 'Software - Infrastructure',
      },
      {
        value: 'communication-equipment',
        label: 'Communication Equipment',
      },
      { value: 'computer-hardware', label: 'Computer Hardware' },
      { value: 'consumer-electronics', label: 'Consumer Electronics' },
      {
        value: 'electronic-components',
        label: 'Electronic Components',
      },
      {
        value: 'electronics-computer-distribution',
        label: 'Electronics & Computer Distribution',
      },
      {
        value: 'scientific-technical-instruments',
        label: 'Scientific & Technical Instruments',
      },
      {
        value: 'semiconductor-equipment-materials',
        label: 'Semiconductor Equipment & Materials',
      },
      { value: 'semiconductors', label: 'Semiconductors' },
      { value: 'solar', label: 'Solar' },
    ],
  },
  {
    value: 'utilities',
    label: 'Utilities',
    industries: [
      {
        value: 'utilities-independent-power-producers',
        label: 'Utilities - Independent Power Producers',
      },
      { value: 'utilities-renewable', label: 'Utilities - Renewable' },
      {
        value: 'utilities-regulated-water',
        label: 'Utilities - Regulated Water',
      },
      {
        value: 'utilities-regulated-electric',
        label: 'Utilities - Regulated Electric',
      },
      {
        value: 'utilities-regulated-gas',
        label: 'Utilities - Regulated Gas',
      },
      {
        value: 'utilities-diversified',
        label: 'Utilities - Diversified',
      },
    ],
  },
];

export function isSearchMarketCapCategory(
  value: string,
): value is SearchMarketCapCategory {
  return SEARCH_MARKET_CAP_CATEGORIES.includes(
    value as SearchMarketCapCategory,
  );
}

export function isSearchExchangeCode(
  value: string,
): value is SearchExchangeCode {
  return SEARCH_EXCHANGE_CODES.includes(value as SearchExchangeCode);
}

export const FUND_CATEGORY_OPTIONS: FacetOption[] = [
  { value: '', label: 'Any Category' },
  ...Object.entries(FUND_CATEGORY_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];
